import type { VideoServerInterface, VSIReturn } from "./video-server-interface";
import { decodeJwt } from "../util";
import { isNullUndefinedOrEmpty } from "../util";

export const presentationStates = {
  LAUNCHED: "OpenForRecord",
  RECORDING: "Recording",
  RECORDED: "Recorded",
} as const;

const LOCAL_STORAGE_TOKEN = "MediasiteST";
export const LOCAL_STORAGE_SITE_ID = "MediasiteSI";
export const LOCAL_STORAGE_SITE_NAME = "MediasiteSN";
export const PAUSE_REFRESH_NEEDED_MILLISECONDS = 4 * 60 * 1000;

interface TokenResponse {
  SessionToken: string;
  SiteId: string;
  SiteName: string;
}

export type LastBytes = {
  byte: Blob | null;
  index: number;
  length: number;
}

export default class MvpVideoServer implements VideoServerInterface {
  #webcamFileName: string | null = null;
  #webcamBlobSent: boolean = false;
  #desktopFileName: string | null = null;
  #desktopBlobSent: boolean = false;
  #sfApiKey: string | null = null;
  #launchToken: string | null = null;
  #sessionToken: string | null = null;
  #serverUrl: string | null = null;
  #presentationUrl: string | null = null;
  #uploadUrl: string | null = null;
  #createMediaUploadUrl: string | null = null;
  #createPendingFileDeletesUrl: string | null = null;
  #exchangeTokenUrl: string | null = null;
  #desktopLongPauseData: LastBytes | null = null;
  #webcamLongPauseData: LastBytes | null = null;
  #startTrim: number | null = null;
  #endTrim: number | null = null;
  #isInitialized: boolean = false;

  /* **************** VideoServerInterface Implementation **************** */

  async initialize(serverUrl: string, apiKey: string, token: string, presentationId: string):
    Promise<VSIReturn> {
    this.#serverUrl = serverUrl;
    this.#sfApiKey = apiKey;
    this.#launchToken = token;
    this.#presentationUrl = `${serverUrl}/Api/v1/Presentations('${presentationId}')`;
    this.#exchangeTokenUrl = `${serverUrl}/api/v1/Home/ExchangeLaunchToken`;
    let ret: VSIReturn = await this._getSessionToken();

    if (ret.successful) {
      ret = await this._fetchMediasiteProperties(presentationId);
    }

    if (ret.successful) {
      // Everything ahead of setting this should be what it needs to be.
      // isInitialized is going to take the place of endless checks if everything is set.
      this.#isInitialized = true;
    }

    return ret;
  }

  async sendVideoBlob(media: Blob, deviceType: string): Promise<Response> {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before sending video blobs.");
    }

    return this._uploadVideoBlob(media, deviceType);
  }

  async generateNewRecordingName(deviceType: string): Promise<string> {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before generating recording names.");
    }

    let fileName = this._generateNewRecordingName(deviceType);
    const succeeded = await this._createNewFileDeletionRecords({ name: fileName });

    if (succeeded === false) {
      fileName = "";
    }

    if (deviceType === "desktop") {
      this.#desktopFileName = fileName;
    } else if (deviceType === "video") {
      this.#webcamFileName = fileName;
    }

    return fileName;
  }

  getUploadUrl(deviceType: string): string {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before getting upload urls.");
    }

    if (deviceType === "video") {
      return `${this.#uploadUrl}/${this.#webcamFileName}`;
    }

    if (deviceType === "desktop") {
      return `${this.#uploadUrl}/${this.#desktopFileName}`;
    }

    return "";
  }

  addXhrAttributes(xhr: XMLHttpRequest): void {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before adding XHR attributes.");
    }

    xhr.setRequestHeader("sfapikey", this.#sfApiKey!);
    xhr.setRequestHeader("Authorization", `Bearer ${this.#sessionToken}`);
  }

  async finishUpload(): Promise<Response> {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before finishing upload.");
    }

    return await this._completePresentationUpload();
  }

  cleanupOnSuccessfulUpload(): void {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before cleaning up on successful upload.");
    }

    this._removeSessionToken();
  }

  async pauseUpload(deviceType: string): Promise<void> {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before pausing upload.");
    }

    await this._getLastByte(deviceType);
  }

  async keepUploadAlive(deviceType: string): Promise<void> {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before keeping upload alive.");
    }

    await this._setLastByte(deviceType);
    console.log(`Kept upload alive for ${deviceType}`);
  }

  async changePresentationState(state: string): Promise<void> {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before changing presentation state.");
    }

    return await this._changePresentationState(state);
  }

  resumeUpload(deviceType: string): void {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before resuming upload.");
    }

    this._clearPauseData(deviceType);
  }

  setTrimPoints(start: number | null, end: number | null): void {
    if (!this.#isInitialized) {
      throw new Error("Video server must be initialized before setting trim points.");
    }

    this.#startTrim = start;
    this.#endTrim = end;
  }

  /* **************** MVP Server Specific Interactions **************** */

  async _changePresentationState(
    state: string,
  ): Promise<void> {

    if (this.#sfApiKey === null || this.#presentationUrl === null || this.#sessionToken === null) {
      console.log("changePresentationState encountered error: presentationUrl, sfApiKey, or sessionToken is null");
      return;
    }

    try {
      await fetch(this.#presentationUrl, {
        method: "PATCH",
        headers: {
          sfapikey: this.#sfApiKey,
          Authorization: `Bearer ${this.#sessionToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Status: state,
        }),
      });
    } catch (error) {
      console.log(`changePresentationState encountered error: ${(error as Error).message}`);
    }
  }

  async _fetchMediasiteProperties(presentationId: string): Promise<VSIReturn> {
    const ret: VSIReturn = {
      successful: false,
      translationNeeded: false,
      errorMessage: null,
    };

    try {
      const url = this.#presentationUrl!;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          sfapikey: this.#sfApiKey!,
          Authorization: `Bearer ${this.#sessionToken}`,
        },
      });
      const responseJson =
        response.headers.get("content-type")?.indexOf("application/json") !== -1
          ? await response.json()
          : {};

      if (responseJson["#Upload"] === undefined ||
        responseJson["#Upload"]?.target === undefined) {
        this.#uploadUrl = `${this.#serverUrl}/FileServer/Presentation/${presentationId}`;
      } else {
        this.#uploadUrl = responseJson["#Upload"].target;
      }

      if (responseJson["#CreateMediaUploadUrl"] === undefined ||
        responseJson["#CreateMediaUploadUrl"]?.target === undefined) {
        this.#createMediaUploadUrl = `${this.#serverUrl}/Api/v1/Presentations('${presentationId}')/CreateMediaUpload`;
      } else {
        this.#createMediaUploadUrl = responseJson["#CreateMediaUploadUrl"].target;
      }

      if (responseJson["#CreatePendingFileDeletes"] === undefined ||
        responseJson["#CreatePendingFileDeletes"]?.target === undefined) {
        this.#createPendingFileDeletesUrl =
          `${this.#serverUrl}/Api/v1/Presentations('${presentationId}')/CreatePendingFileDeletes`;
      } else {
        this.#createPendingFileDeletesUrl = responseJson["#CreatePendingFileDeletes"].target;
      }

      ret.successful = true;
    } catch (error) {
      console.log(`fetchMediasiteProperties encountered error: ${(error as Error).message}`);
      ret.translationNeeded = true;

      if (
        (error as Error).message.includes("Failed to fetch") ||
        (error as Error).message.includes("NetworkError when attempting to fetch resource")
      ) {
        ret.errorMessage = "cors-error-message";
      } else {
        ret.errorMessage = "general-error-message";
      }
    }

    return ret;
  }

  _removeSessionToken(): void {
    try {
      window.localStorage.removeItem(LOCAL_STORAGE_TOKEN);
    } catch (error) {
      console.log(`removeSessionToken encountered error: ${(error as Error).message}`);
    }
  }

  _isSessionTokenExpired(sessionToken: string | null | undefined): boolean {
    if (sessionToken === undefined || sessionToken === "" ||
      sessionToken === null) {
      return true;
    }

    const decodedToken = decodeJwt(sessionToken);
    const now = Math.floor(new Date().getTime() / 1000);
    const nbf: number | null = decodedToken?.payload.nbf as number;
    const exp: number | null = decodedToken?.payload.exp as number;
    return typeof nbf !== "number" || typeof exp !== "number" || now < nbf || now > exp;
  }

  async _getSessionToken(): Promise<VSIReturn> {
    const ret: VSIReturn = {
      successful: false,
      translationNeeded: false,
      errorMessage: null,
    };
    let resp: TokenResponse | undefined;
    let sessionToken: string | null = null;
    let siteId: string | null = null;
    let siteName: string | null = null;
    let isNewTokenNeeded = false;
    let decodedToken;

    if (this.#serverUrl === null || this.#sfApiKey === null) {
      console.log(this.#serverUrl);
      console.log(this.#sfApiKey);
      return ret;
    }

    try {
      sessionToken = window.localStorage.getItem(LOCAL_STORAGE_TOKEN);
      siteId = window.localStorage.getItem(LOCAL_STORAGE_SITE_ID);
      siteName = window.localStorage.getItem(LOCAL_STORAGE_SITE_NAME);
      isNewTokenNeeded = this._isSessionTokenExpired(sessionToken) === true;

      if (isNewTokenNeeded) {
        resp = await this._exchangeLaunchToken(this.#launchToken!);
        sessionToken = resp.SessionToken;
        decodedToken = decodeJwt(sessionToken);

        if (decodedToken !== null) {
          siteId = decodedToken.payload.siteid;
          siteName = decodedToken.payload.sitename;
        }
      }

      isNewTokenNeeded = (await this._isTokenValid(sessionToken)) === false;

      if (isNewTokenNeeded) {
        resp = await this._exchangeLaunchToken(this.#launchToken!);
        sessionToken = resp.SessionToken;
        decodedToken = decodeJwt(sessionToken);

        if (decodedToken !== null) {
          siteId = decodedToken.payload.siteid;
          siteName = decodedToken.payload.sitename;
        }
      }

      if (sessionToken) {
        window.localStorage.setItem(LOCAL_STORAGE_TOKEN, sessionToken);
        this.#sessionToken = sessionToken;
      }

      if (siteId) {
        window.localStorage.setItem(LOCAL_STORAGE_SITE_ID, siteId);
      }

      if (siteName) {
        window.localStorage.setItem(LOCAL_STORAGE_SITE_NAME, siteName);
      }

      ret.successful = true;
    } catch (error) {
      console.log(`getSessionToken encountered error: ${(error as Error).message}`);

      if (
        (error as Error).message.includes("Failed to fetch") ||
        (error as Error).message.includes("NetworkError when attempting to fetch resource")
      ) {
        ret.errorMessage = "cors-error-message";
      } else {
        ret.errorMessage = "general-error-message";
      }
    }

    return ret;
  }

  async _exchangeLaunchToken(
    launchToken: string,
  ): Promise<TokenResponse> {
    try {
      const url = this.#exchangeTokenUrl!;
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify({
          LaunchToken: launchToken,
        }),
        headers: {
          sfapikey: this.#sfApiKey!,
          "Content-Type": "application/json",
        },
      });

      const responseJson =
        response.headers.get("content-type")?.indexOf("application/json") !== -1
          ? await response.json()
          : {};
      const sessionToken = responseJson.SessionToken as string;
      const siteId = responseJson.siteid === undefined ? "" : (responseJson.siteid as string);
      const siteName = responseJson.sitename === undefined ? "" : (responseJson.sitename as string);

      return {
        SessionToken: sessionToken,
        SiteId: siteId,
        SiteName: siteName,
      };
    } catch (error) {
      console.log(`exchangeLaunchToken encountered error: ${(error as Error).message}`);
      throw error;
    }
  }

  async _isTokenValid(sessionToken: string | null): Promise<boolean> {
    if (sessionToken === null) {
      return false;
    }

    try {
      const url = this.#presentationUrl!;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          sfapikey: this.#sfApiKey!,
          Authorization: `Bearer ${sessionToken}`,
        },
      });

      return response.status !== 401;
    } catch (error) {
      console.log(`isTokenValid encountered an error: ${(error as Error).message}`);
      throw error;
    }
  }

  _generateNewRecordingName(deviceType: string): string {
    const guid: string = crypto.randomUUID();
    const prefix = deviceType === "desktop" ? "display" : "camera";
    return `${prefix}_${guid}.webm`;
  }

  _getUpidFromToken(token: string | null): string | null {
    const ret = decodeJwt(token);

    if (ret === null || ret.payload === null || ret.payload.upid === null) {
      return null;
    }

    let upid: string = ret.payload.upid;

    if (upid.includes("-") === false) {
      upid = upid.substring(0, upid.length - 2);
      // The next line looks dumb. Blame eslint"s line length constraint of 120 characters.
      upid = `${upid.substring(0, 8)}-${upid.substring(8, 12)}-${upid.substring(12, 16)}-${upid.substring(16, 20)}-` +
        `${upid.substring(20)}`;
    }

    return upid;
  }

  async _getLastByte(deviceType: string): Promise<void> {
    const lastBytes: LastBytes = {
      byte: null,
      index: 0,
      length: 0,
    };

    if (isNullUndefinedOrEmpty(deviceType)) {
      throw new Error("Device type must be provided to get last byte.");
    } else if (deviceType !== "desktop" && deviceType !== "video") {
      throw new Error("Device type must be either desktop or video.");
    }

    if ((deviceType === "desktop" && this.#desktopLongPauseData !== null) ||
      (deviceType === "video" && this.#webcamLongPauseData !== null)) {
      console.log("Long pause data already exists for device type: " + deviceType);
      return;
    }

    let fileName: string = this.#webcamFileName!;

    if (deviceType === "desktop") {
      fileName = this.#desktopFileName!;
    }

    try {
      const sizeResponse = await fetch(`${this.#uploadUrl}/${fileName}`, {
        method: "PROPFIND",
        body: "<?xml version='1.0' encoding='UTF-8'?><d:propfind xmlns:d=\"DAV:\">" +
          "<d:prop><d:resourcetype/></d:prop></d:propfind>",
        headers: {
          Depth: "0",
          "Content-Type": "text/xml",
          sfapikey: this.#sfApiKey!,
          Authorization: `Bearer ${this.#sessionToken}`,
        },
      });

      if (sizeResponse.status === 207) {
        const xmlString = await sizeResponse.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "application/xml");
        const contentLengthElement = xmlDoc.getElementsByTagName("d:getcontentlength")[0];
        if (contentLengthElement) {
          lastBytes.length = parseInt(contentLengthElement.textContent || "0", 10);
          if (lastBytes.length === 0) {
            lastBytes.byte = null;

            if (deviceType === "desktop") {
              this.#desktopLongPauseData = lastBytes;
            } else {
              this.#webcamLongPauseData = lastBytes;
            }
          }
          lastBytes.index = Math.max(0, lastBytes.length - 10); // Prevent negative index
          console.log(lastBytes.length);
          console.log(lastBytes.index);
        }
      }

      const dataResponse = await fetch(`${this.#uploadUrl}/${fileName}`, {
        method: "GET",
        headers: {
          Range: `bytes=${lastBytes.index}-${lastBytes.length - 1}`,
          sfapikey: this.#sfApiKey!,
          Authorization: `Bearer ${this.#sessionToken}`,
        },
      });

      if (dataResponse.status === 206) {
        lastBytes.byte = await dataResponse.blob();
      }
    } catch (error) {
      console.log(`getLastByte encountered an error: ${error}`);
      throw error;
    }

    if (deviceType === "desktop") {
      console.log("Got long pause data for desktop: ", lastBytes);
      this.#desktopLongPauseData = lastBytes;
    } else {
      console.log("Got long pause data for webcam: ", lastBytes);
      this.#webcamLongPauseData = lastBytes;
    }
  }

  async _setLastByte(deviceType: string): Promise<boolean> {
    if ((deviceType === "desktop" && this.#desktopLongPauseData === null) ||
      (deviceType === "video" && this.#webcamLongPauseData === null)) {
      console.log("No long pause data to set for device type: " + deviceType);
      return false;
    }

    const lastByte = deviceType === "desktop" ? this.#desktopLongPauseData!.byte : this.#webcamLongPauseData!.byte;
    const range = deviceType === "desktop" ?
      `${this.#desktopLongPauseData!.index}-${this.#desktopLongPauseData!.length - 1}` :
      `${this.#webcamLongPauseData!.index}-${this.#webcamLongPauseData!.length - 1}`;
    console.log({
      method: "POST",
      body: lastByte,
      headers: {
        "Content-Range": `bytes ${range}/*`,
        "Content-Type": "video/mp4",
        sfapikey: this.#sfApiKey!,
        Authorization: `Bearer ${this.#sessionToken}`,
      },
    });

    if (isNullUndefinedOrEmpty(deviceType)) {
      throw new Error("Device type must be provided to get last byte.");
    } else if (deviceType !== "desktop" && deviceType !== "video") {
      throw new Error("Device type must be either desktop or video.");
    }

    let fileName: string = this.#webcamFileName!;

    if (deviceType === "desktop") {
      fileName = this.#desktopFileName!;
    }

    try {
      const response = await fetch(`${this.#uploadUrl}/${fileName}`, {
        method: "POST",
        body: lastByte,
        headers: {
          "Content-Range": `bytes ${range}/*`,
          "Content-Type": "video/mp4",
          sfapikey: this.#sfApiKey!,
          Authorization: `Bearer ${this.#sessionToken}`,
        },
      });

      return response.status === 201;
    } catch (error) {
      console.log(`setLastByte encountered an error: ${error}`);
      throw error;
    }
  }

  async _createNewFileDeletionRecords({ name }: {
      name: string;
    }) {
    if (isNullUndefinedOrEmpty(this.#createPendingFileDeletesUrl)) {
      throw new Error("createNewFileDeletionRecords is not defined in uploadSettings");
    }

    const parameters = {
      "FileNames": [name],
      "IntervalDays": 7,
    };

    try {
      const options: RequestInit = {
        method: "POST",
        body: JSON.stringify(parameters),
        headers: {
          "sfapikey": this.#sfApiKey!,
          "Authorization": `Bearer ${this.#sessionToken}`,
          "Content-Type": "application/json",
        },
      };

      const response = await fetch(this.#createPendingFileDeletesUrl!, options);

      if (!response.ok) {
        console.log(`Error in createNewFileDeletionRecords: ${response.status}`);
      } else {
        return true;
      }
    } catch (error) {
      console.error("Error in createNewFileDeletionRecords: ", error);
    }

    return false;
  }

  async _completePresentationUpload(): Promise<Response> {
    const downloadNames: string[] = [];

    // Preference desktop video over webcam
    if (isNullUndefinedOrEmpty(this.#desktopFileName) === false) {
      downloadNames.push(this.#desktopFileName!);
    }

    if (isNullUndefinedOrEmpty(this.#webcamFileName) === false) {
      downloadNames.push(this.#webcamFileName!);
    }

    const audioSource = downloadNames[downloadNames.length - 1];
    const url: string = this.#createMediaUploadUrl!;

    if (isNullUndefinedOrEmpty(url)) {
      throw new Error("createMediaUploadUrl is not defined in uploadSettings");
    }

    type CreateMediaUploadUrlParms = {
      FileNames: string[];
      AudioSource: string;
      MediaStart?: number;
      MediaEnd?: number;
    }

    const parameters: CreateMediaUploadUrlParms = {
      "FileNames": downloadNames,
      "AudioSource": audioSource,
    };

    if (this.#startTrim !== null && this.#startTrim > 0.0) {
      parameters.MediaStart = Math.floor(this.#startTrim * 1000.0);
    }

    if (this.#endTrim !== null && this.#endTrim > 0.0) {
      parameters.MediaEnd = Math.ceil(this.#endTrim * 1000.0);
    }

    const options: RequestInit = {
      method: "POST",
      body: JSON.stringify(parameters),
      headers: {
        "sfapikey": this.#sfApiKey!,
        "Authorization": `Bearer ${this.#sessionToken}`,
        "Content-Type": "application/json",
      },
    };

    return await fetch(url, options);
  }

  async _uploadVideoBlob(media: Blob, deviceType: string): Promise<Response> {
    let url: string;
    let httpMethod: string;

    if (deviceType === "video") {
      url = `${this.#uploadUrl}/${this.#webcamFileName}`;

      if (this.#webcamBlobSent) {
        httpMethod = "POST";
      } else {
        httpMethod = "PUT";
        this.#webcamBlobSent = true;
      }
    } else {
      url = `${this.#uploadUrl}/${this.#desktopFileName}`;

      if (this.#desktopBlobSent) {
        httpMethod = "POST";
      } else {
        httpMethod = "PUT";
        this.#desktopBlobSent = true;
      }
    }

    const options: RequestInit = {
      method: httpMethod,
      body: media,
      headers: {
        sfapikey: this.#sfApiKey!,
        Authorization: `Bearer ${this.#sessionToken}`,
      },
    };

    return await fetch(url, options);
  }

  _clearPauseData(deviceType: string): void {
    if (deviceType === "desktop") {
      this.#desktopLongPauseData = null;
    } else {
      this.#webcamLongPauseData = null;
    }
  }
}
